import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * A section of the page as Site & widget's preview shows it: outlined when it
 * is the one being edited, and clickable to edit it.
 *
 * Without an `editor` it is nothing — the live page renders its children as
 * they are. With one, a transparent button covers the section and its own
 * content is made inert: in the preview a click means "edit this", and a
 * booking button that opened a form, or a social link that left the page,
 * would only get in the way of that. A covering button rather than a click
 * handler on the wrapper, so the section's links are never nested inside
 * something that is itself a button.
 */
export const editorShape = PropTypes.shape({
  /** The section being edited: "header", "about", "contact", "social"… */
  active: PropTypes.string,
  /** Within About, the block being edited (its index). */
  activeBlock: PropTypes.number,
  /** Called with a section, and for an About block also its index. */
  onSelect: PropTypes.func.isRequired,
  /** Section id → the name on its badge, in the dashboard's language. */
  labels: PropTypes.objectOf(PropTypes.string).isRequired,
  /** Section id → what an empty section says, where it would be. */
  placeholders: PropTypes.objectOf(PropTypes.string),
});

const EditableSection = ({ editor = null, section, open = false, children }) => {
  const contentRef = useRef(null);

  // React 18 has no `inert` prop; the DOM property is the reliable way to set it.
  useEffect(() => {
    if (contentRef.current) contentRef.current.inert = !open;
  }, [editor, open]);

  if (!editor) return children;

  const active = editor.active === section;
  const label = editor.labels[section] || section;

  // Open: the section being edited, whose own parts take the clicks and carry
  // the outline (the About section: each block is selectable, and the block
  // being edited is the one outlined — two outlines would say it twice).
  if (open) {
    return (
      <div
        data-edit-section={section}
        className="relative scroll-mt-16 md:scroll-mt-18"
      >
        <div ref={contentRef}>{children}</div>
      </div>
    );
  }

  return (
    <div data-edit-section={section} className="group/edit relative scroll-mt-16 md:scroll-mt-18">
      <div ref={contentRef}>{children}</div>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        onClick={() => editor.onSelect(section)}
        className={`absolute inset-0 z-10 cursor-pointer bg-transparent outline-offset-[-3px] focus-visible:outline-3 focus-visible:outline-[var(--site1-primary)] ${
          active
            ? "outline-3 outline-[var(--site1-primary)]"
            : "hover:outline-2 hover:outline-dashed hover:outline-[var(--site1-primary)]"
        }`}
      >
        <span
          className={`pointer-events-none absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-md bg-[var(--site1-primary)] px-2.5 py-1 text-xs font-semibold text-[var(--site1-on-primary)] shadow-card transition-opacity motion-reduce:transition-none ${
            active
              ? "opacity-100"
              : "opacity-0 group-hover/edit:opacity-100 group-focus-within/edit:opacity-100"
          }`}
        >
          {label}
          <i className="bi bi-pencil" aria-hidden="true" />
        </span>
      </button>
    </div>
  );
};

EditableSection.propTypes = {
  editor: editorShape,
  section: PropTypes.string.isRequired,
  /** Outline it, but let its content take the clicks. */
  open: PropTypes.bool,
  children: PropTypes.node,
};

export default EditableSection;
